/**
 * Settings Service
 * API operations for user settings (API service)
 */
import { apiClient } from './apiClient';
import { API_ENDPOINTS } from '@/constants/api';
import type {
  UpdateSettingRequest,
  BatchUpdateSettingsRequest,
} from '@/types/api/requests';
import type {
  AllSettingsResponse,
  SettingResponse,
} from '@/types/api/responses';

/** Allowed setting keys */
export type SettingKey =
  | 'language'
  | 'theme'
  | 'timezone'
  | 'notifications'
  | 'map_style'
  | 'default_zoom'
  | 'filters'
  | 'note_filters'
  | 'ui'
  | 'search'
  | 'guides'
  | 'map'
  | 'basemap_by_workspace'
  | 'last_workspace';

export const settingsService = {
  /**
   * Get all user settings
   */
  getAll: () => apiClient.get<AllSettingsResponse>(API_ENDPOINTS.SETTINGS.BASE),

  /**
   * Get a specific setting by key
   */
  get: (key: SettingKey) =>
    apiClient.get<SettingResponse>(API_ENDPOINTS.SETTINGS.BY_KEY(key)),

  /**
   * Update a specific setting
   */
  update: (key: SettingKey, data: UpdateSettingRequest) =>
    apiClient.put<SettingResponse>(API_ENDPOINTS.SETTINGS.BY_KEY(key), data),

  /**
   * Delete a specific setting (resets to default)
   */
  delete: (key: SettingKey) =>
    apiClient.delete(API_ENDPOINTS.SETTINGS.BY_KEY(key)),

  /**
   * Batch update multiple settings
   */
  batchUpdate: (data: BatchUpdateSettingsRequest) =>
    apiClient.post<AllSettingsResponse>(API_ENDPOINTS.SETTINGS.BATCH, data),
};
